import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  Icon,
  RiskSeverityChip,
  RiskStatusChip,
  RowMenu,
  VISIBILITY_LABELS,
  formatDateOnly,
  todayYmd,
  visibleRisks,
  useToast,
  type IconName,
  type RiskStatus,
  type RoomItemVisibility,
  type RoomRisk,
  type RowMenuItem,
} from '@dataroom/shared';
import { useCreateRisk, useRisks, useUpdateRisk } from '@/api/hooks';
import { apiMessage } from '@/lib/errors';
import { RiskDialog } from './RiskDialog';

const VISIBILITY_ICON: Record<RoomItemVisibility, IconName> = {
  deal_team: 'lock',
  seller: 'users',
  all: 'eye',
};

/**
 * The room's RISKS & ISSUES log (ADR-0047): what could affect the deal, who owns
 * it, when it is due and who may see it. Open and mitigating entries come first
 * (high severity first); resolved ones are hidden until "Show resolved". The
 * admin adds, edits and closes entries — there is no delete, so the record
 * stays. Written by people: the AI never adds or judges an entry.
 */
export function RisksLogCard({ roomId, readOnly }: { roomId: string; readOnly: boolean }) {
  const risks = useRisks(roomId);
  const create = useCreateRisk(roomId);
  const update = useUpdateRisk(roomId);
  const toast = useToast();
  const [showResolved, setShowResolved] = useState(false);
  const [dialog, setDialog] = useState<{ mode: 'add' } | { mode: 'edit'; risk: RoomRisk } | null>(
    null,
  );

  const all = risks.data?.risks ?? [];
  const ownerOptions = risks.data?.ownerOptions ?? [];
  const shown = visibleRisks(all, showResolved);
  const resolvedCount = all.filter((r) => r.status === 'resolved').length;

  function closeDialog() {
    create.reset();
    update.reset();
    setDialog(null);
  }

  function setStatus(risk: RoomRisk, status: RiskStatus) {
    update.mutate(
      { riskId: risk.id, body: { status } },
      {
        onSuccess: () =>
          toast({
            message:
              status === 'resolved'
                ? `Closed “${risk.title}”`
                : status === 'open'
                  ? `Reopened “${risk.title}”`
                  : `Marked “${risk.title}” mitigating`,
          }),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle>Risks &amp; issues</CardTitle>
            <p className="text-sm text-muted-foreground">
              Anything that could affect the deal — who owns it and when it should be dealt with.
              Entries are written by the deal team; the AI never adds or judges them. Open entries
              go out in the weekly digest to the people who can see them.
            </p>
          </div>
          {readOnly ? null : (
            <Button size="sm" onClick={() => setDialog({ mode: 'add' })}>
              <Icon name="plus" size={14} />
              Add entry
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex w-fit items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Show resolved{resolvedCount > 0 ? ` (${resolvedCount})` : ''}
        </label>

        {update.error && !dialog ? (
          <p className="text-sm text-destructive">{apiMessage(update.error)}</p>
        ) : null}

        {risks.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : risks.error ? (
          <ErrorText error={risks.error} />
        ) : shown.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {all.length === 0
              ? 'Nothing recorded yet. Add an entry when something could hold up or change the deal.'
              : 'No open entries — everything recorded so far is resolved.'}
          </p>
        ) : (
          <ul className="divide-y rounded-md border" aria-label="Risks and issues">
            {shown.map((risk) => (
              <RiskRow
                key={risk.id}
                risk={risk}
                readOnly={readOnly}
                busy={update.isPending && update.variables?.riskId === risk.id}
                onEdit={() => setDialog({ mode: 'edit', risk })}
                onStatus={(status) => setStatus(risk, status)}
              />
            ))}
          </ul>
        )}
      </CardContent>

      {dialog?.mode === 'add' ? (
        <RiskDialog
          ownerOptions={ownerOptions}
          pending={create.isPending}
          error={apiMessage(create.error)}
          onClose={closeDialog}
          onCreate={(body) =>
            create.mutate(body, {
              onSuccess: (created) => {
                setDialog(null);
                toast({ message: `Added “${created.title}”` });
              },
            })
          }
        />
      ) : dialog?.mode === 'edit' ? (
        <RiskDialog
          risk={dialog.risk}
          ownerOptions={ownerOptions}
          pending={update.isPending}
          error={apiMessage(update.error)}
          onClose={closeDialog}
          onUpdate={(body) =>
            update.mutate(
              { riskId: dialog.risk.id, body },
              {
                onSuccess: (saved) => {
                  setDialog(null);
                  toast({ message: `Saved “${saved.title}”` });
                },
              },
            )
          }
        />
      ) : null}
    </Card>
  );
}

function RiskRow({
  risk,
  readOnly,
  busy,
  onEdit,
  onStatus,
}: {
  risk: RoomRisk;
  readOnly: boolean;
  busy: boolean;
  onEdit: () => void;
  onStatus: (status: RiskStatus) => void;
}) {
  const overdue = risk.status !== 'resolved' && risk.dueDate !== null && risk.dueDate < todayYmd();
  const items: RowMenuItem[] = [{ label: 'Edit…', icon: 'pen', onSelect: onEdit, disabled: busy }];
  if (risk.status === 'open') {
    items.push({
      label: 'Mark mitigating',
      icon: 'refresh',
      onSelect: () => onStatus('mitigating'),
    });
  }
  if (risk.status === 'resolved') {
    items.push({ label: 'Reopen', icon: 'undo', onSelect: () => onStatus('open') });
  } else {
    items.push({ label: 'Close as resolved', icon: 'check', onSelect: () => onStatus('resolved') });
  }

  return (
    <li className="flex items-start gap-3 p-4" data-risk-id={risk.id}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <RiskSeverityChip severity={risk.severity} />
          <RiskStatusChip status={risk.status} />
          <span className="min-w-0 break-words text-sm font-semibold">{risk.title}</span>
        </div>
        {risk.description ? (
          <p className="whitespace-pre-wrap break-words text-sm text-ink-2">{risk.description}</p>
        ) : null}
        <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <div className="flex gap-1">
            <dt>Owner:</dt>
            <dd className="text-ink-2">{risk.owner ?? 'No owner yet'}</dd>
          </div>
          <div className="flex gap-1">
            <dt>Due:</dt>
            <dd className={overdue ? 'font-semibold text-danger' : 'text-ink-2'}>
              {risk.dueDate ? formatDateOnly(risk.dueDate) : 'No date'}
              {overdue ? ' (overdue)' : ''}
            </dd>
          </div>
          {risk.visibility ? (
            <div className="flex items-center gap-1">
              <dt className="sr-only">Who can see it:</dt>
              <dd className="inline-flex items-center gap-1 text-ink-2">
                <Icon name={VISIBILITY_ICON[risk.visibility]} size={12} />
                {VISIBILITY_LABELS[risk.visibility]}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
      {readOnly ? null : <RowMenu label={risk.title} items={items} />}
    </li>
  );
}
